import React, { FC, useRef, useState, useEffect } from 'react';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { useData } from '@hooks/useData';
import { Space } from '@components/atoms/Space';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { RoundProfilePicture } from '@components/atoms/RoundProfilePicture';
import styles from './ChangeLanguage.module.css';
import { Button, ButtonMode } from '@components/atoms/Button';
import { LANGUAGES, Language } from '@constants';
import { useChangeLanguage } from '@services/useChangeLanguage';
import { useNotifications } from '@hooks/useNotifications';

export const ChangeLanguage: FC = () => {
    const { t } = useTranslation();
    const pickerRef = useRef<HTMLDivElement>(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const { language, setLanguage } = useData();
    const [selectedLanguage, setSelectedLanguage] = useState<Language>(language || 'en');
    const { request, lastResponseTime, error, success } = useChangeLanguage();
    const { showDefaultNotification } = useNotifications();

    const handleOnClick = () => {
        setPickerOpen(true);
    };

    const handleSelectLang = (lang: Language) => {
        request({ language: lang });
        setSelectedLanguage(lang);
    };

    useEffect(() => {
        if (lastResponseTime) {
            if (error) {
                showDefaultNotification({ type: 'error', message: error });
            } else if (success) {
                setLanguage(selectedLanguage);

                i18n.changeLanguage(selectedLanguage);
                setPickerOpen(false);
                showDefaultNotification({ type: 'success', message: success });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime, success]);

    useEffect(() => {
        if (!pickerOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
                setPickerOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [pickerOpen]);

    return (
        <>
            <Space spaceBreakpoints={{ xs: { space: 30 } }} />
            <span
                className="d-flex justify-content-between align-items-center"
                onClick={handleOnClick}
                style={{ cursor: 'pointer' }}>
                <Typography fontSize="16" fontWeight="300">
                    <>
                        <Typography fontWeight="300" tagVariant={TypographyTagVariants.span}>
                            <i style={{ marginRight: '5px' }} className="fa-thin fa-language"></i>
                        </Typography>
                        {t('user-area.language')}
                    </>
                </Typography>
                <Typography color="muted">{language}</Typography>
            </span>
            <>
                {pickerOpen && (
                    <div className={styles.overlay}>
                        <div className={styles.pickerContainer} ref={pickerRef}>
                            <RoundProfilePicture
                                icon="fa-thin fa-language"
                                labelText={t('user-area.language-label')}
                                hintText={t('user-area.language-hint')}
                            />
                            <Space spaceBreakpoints={{ xs: { space: 30 } }} />

                            {LANGUAGES.map((lang) => (
                                <React.Fragment key={lang.code}>
                                    <Button
                                        label={lang.code}
                                        mode={ButtonMode.primary}
                                        classes={styles.button}
                                        disabled={language === lang.code}
                                        onClick={() => handleSelectLang(lang.code)}
                                    />
                                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                )}
            </>
        </>
    );
};
